import { api, publicApi, selfAppliedApi } from '@/api'
import { BaseApi } from '@/api/BaseApi.ts'
import type {
  CollectAuditData,
  CollectAuditFetchParams,
  CollectFetchData,
  CollectFormData,
  ExportPdfParams,
  ExportPdfResponse,
  GetCollectSharedData,
  PostTOTPResponse,
  RelatedFeatureOutput,
} from '@/api/Collect/types.ts'
import type { FetchParams, Paginated, ParamType } from '@/api/types.ts'

class Api extends BaseApi<
  GetCollectSharedData,
  Paginated<CollectFetchData>,
  CollectFormData,
  CollectAuditFetchParams
> {
  readonly baseUrlWithoutView =
    '/initiatives/:initiativeId/features/:featureId/collects'

  readonly baseUrl =
    '/initiatives/:initiativeId/features/:featureId/collects/:viewMode'

  async create(
    params: ParamType[],
    payload: CollectFormData,
    publicAccess?: boolean
  ): Promise<{ id: number }> {
    if (publicAccess) {
      const { data } = await selfAppliedApi.post<{ id: number }>(
        'features-public/collects',
        payload,
        this.config
      )
      return data
    }
    const url = this.getBaseUrl(`${this.baseUrl}/:version`, params)
    const { data } = await api.post<{ id: number }>(url, payload, this.config)
    return data
  }

  async update(
    id: ParamType | ParamType[],
    payload: CollectFormData
  ): Promise<unknown> {
    const params = this.toParamArray(id)
    const url = this.getBaseUrl(`${this.baseUrl}/:version/:id`, params)
    const { data } = await api.put<unknown>(url, payload, this.config)
    return data
  }

  async fetchAudits(
    id: ParamType | ParamType[],
    queryParams: FetchParams
  ): Promise<Paginated<CollectAuditData>> {
    const filteredParams: Record<string, string> =
      this.buildFetchParams(queryParams)

    const URParams = new URLSearchParams(filteredParams)
    const params = this.toParamArray(id)
    const url = this.getBaseUrl(
      `${this.baseUrl}/:id/audits?${URParams.toString()}`,
      params
    )
    const { data } = await api.get<Paginated<CollectAuditData>>(
      url,
      this.config
    )
    return data
  }

  async get(
    id: ParamType | ParamType[],
    version?: ParamType
  ): Promise<GetCollectSharedData> {
    const filteredParams: Record<string, string> = this.buildFetchParams({
      version,
    })

    const URParams = new URLSearchParams(filteredParams)
    const params = this.toParamArray(id)
    const url = this.getBaseUrl(`${this.baseUrl}/:id?${URParams.toString()}`, [
      ...params,
    ])
    const { data } = await api.get<GetCollectSharedData>(url, this.config)
    return data
  }

  override async fetch(
    params: ParamType[],
    queryParams: CollectAuditFetchParams = {} as CollectAuditFetchParams
  ): Promise<Paginated<CollectFetchData>> {
    const filteredParams: Record<string, string> =
      this.buildFetchParams(queryParams)

    const URParams = new URLSearchParams(filteredParams)
    const url = `${this.getBaseUrl(this.baseUrlWithoutView, params)}?${URParams.toString().replaceAll('+', '%20')}`
    const { data } = await api.get<Paginated<CollectFetchData>>(
      url,
      this.config
    )
    return data
  }

  async exportPdf(
    params: ParamType[],
    queryParams: ExportPdfParams
  ): Promise<ExportPdfResponse> {
    const filteredParams: Record<string, string> =
      this.buildFetchParams(queryParams)
    const URParams = new URLSearchParams(filteredParams)
    const url = this.getBaseUrl(
      `${this.baseUrlWithoutView}/:platform/:collectId/pdf?${URParams.toString()}`,
      params
    )
    const { data } = await api.get<ExportPdfResponse>(url, this.config)
    return data
  }

  async execAuthOtp(key: string, otp: string): Promise<PostTOTPResponse> {
    const { data } = await publicApi.post<PostTOTPResponse>(
      `collect/${key}/otp`,
      { otp }
    )
    return data
  }

  async getDescendingRelations(
    params: ParamType[]
  ): Promise<RelatedFeatureOutput[]> {
    const url = this.getBaseUrl(
      `${this.baseUrlWithoutView}/:collectId/relations/descending`,
      params
    )
    const { data } = await api.get<RelatedFeatureOutput[]>(url, this.config)
    return data
  }

  async getAscendingRelations(
    params: ParamType[]
  ): Promise<RelatedFeatureOutput[]> {
    const url = this.getBaseUrl(
      `${this.baseUrlWithoutView}/:collectId/relations/ascending`,
      params
    )
    const { data } = await api.get<RelatedFeatureOutput[]>(url, this.config)
    return data
  }
}

const CollectApi = new Api()

export default CollectApi
